import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  ChevronLeft,
  Eye,
  MessageCircle,
  ShoppingBag,
  X,
  Plus,
  ChevronUp,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Share2,
  Heart,
} from "lucide-react";
import {
  getStreamById,
  getStreamProducts,
  subscribeToStreamPurchases,
  trackViewerJoin,
  trackViewerLeave,
} from "@/services/liveStreamService";
import AgoraRTC from "agora-rtc-sdk-ng";
import { appwriteConfig, client, functions } from "@/lib/appwrite/config";
import { databases } from "@/lib/appwrite/config";
import { Models, RealtimeResponseEvent } from "appwrite";

import { Product, Stream } from "@/types/liveStreamTypes";
import LiveStreamProducts from "@/components/LiveSelling/LiveStreamProducts";
import LiveStreamChat from "@/components/LiveSelling/LiveStreamChat";
import {
  useCheckFollowStatus,
  useFollowUser,
  useUnfollowUser,
} from "@/lib/react-query/queries";
import { toast } from "../ui/use-toast";

const APP_ID = "c3d54d05b3764b7d8766eecfbb3dd90d";

interface LiveStreamViewerProps {
  streamId: string;
  userId: string;
  userName: string;
}

interface PurchaseData {
  productId: string;
  orderId: string;
  paymentId: string;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  text: string;
  userRole: string;
  timestamp: string;
  priority?: "normal" | "question" | "purchase";
}

interface FloatingMessage extends ChatMessage {
  isVisible: boolean;
  displayUntil: number;
}

const LiveStreamViewer: React.FC<LiveStreamViewerProps> = ({
  streamId,
  userId,
  userName,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [stream, setStream] = useState<Stream | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featuredProductId, setFeaturedProductId] = useState<string | null>(
    null
  );
  const [purchaseSuccess, setPurchaseSuccess] = useState<{
    productName?: string;
    productId: string;
    orderId: string;
    paymentId: string;
  } | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);

  // UI states
  const [, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showStreamDetails, setShowStreamDetails] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [localFollowStatus, setLocalFollowStatus] = useState<any>(null);

  // 🔧 FIXED: Enhanced chat state management for real-time updates
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [lastViewedMessageCount, setLastViewedMessageCount] = useState(0);

  const handleChatMessagesUpdate = useCallback(
    (messages: ChatMessage[]) => {
      setChatMessages(messages);

      // 🔧 FIXED: Update message count for real-time badge updates
      if (!showChat && messages.length > lastViewedMessageCount) {
        setNewMessageCount(messages.length - lastViewedMessageCount);
      }
    },
    [showChat, lastViewedMessageCount]
  );

  // 🔧 FIXED: Enhanced floating messages with proper cleanup
  const [floatingMessages, setFloatingMessages] = useState<FloatingMessage[]>(
    []
  );
  const [earlyBirdDismissed, setEarlyBirdDismissed] = useState(false);
  const floatingMessageTimeouts = useRef<Map<string, NodeJS.Timeout>>(
    new Map()
  );

  // Agora and tracking refs
  const clientRef = useRef<any>(null);
  const localTracksRef = useRef<any[]>([]);
  const agoraContainerRef = useRef<HTMLDivElement>(null);
  const initializationInProgress = useRef(false);
  const tokenRefreshTimer = useRef<NodeJS.Timeout>();
  const joinedStreamRef = useRef(false);
  const viewerTrackingRef = useRef(false);
  const componentMountedRef = useRef(false);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [tokenInfo, setTokenInfo] = useState<{
    token: string;
    expiresAt: number;
  } | null>(null);

  // 🔧 FIXED: Real-time viewer count and stream status
  const [viewerCount, setViewerCount] = useState<number>(0);
  const [streamStatus, setStreamStatus] = useState<"live" | "ended">("live");

  // 🔧 FIXED: Real-time subscription refs for cleanup
  const realtimeSubscriptions = useRef<Array<() => void>>([]);

  const { data: followStatus } = useCheckFollowStatus(
    userId,
    stream?.sellerId || ""
  );
  const { mutate: followUser } = useFollowUser();
  const { mutate: unfollowUser } = useUnfollowUser();

  // Determine if the user is the stream creator
  const isCreator = stream && userId === stream.sellerId;

  useEffect(() => {
    if (followStatus !== undefined) {
      setLocalFollowStatus(followStatus);
    }
  }, [followStatus]);

  const handleFollow = useCallback(async () => {
    if (!stream || !userId || userId === stream.sellerId) return;

    setIsFollowLoading(true);
    const isCurrentlyFollowing = !!localFollowStatus;

    // 🔧 FIXED: Store previous state for error recovery, but don't do optimistic update
    const previousFollowStatus = localFollowStatus;

    try {
      if (isCurrentlyFollowing && localFollowStatus?.$id) {
        // Unfollow action
        unfollowUser(localFollowStatus.$id, {
          onSuccess: () => {
            setIsFollowLoading(false);
            setLocalFollowStatus(null);
            toast({
              title: "Success",
              description: `You unfollowed ${stream.sellerName || "this user"}`,
            });
          },
          onError: (error) => {
            setIsFollowLoading(false);
            setLocalFollowStatus(previousFollowStatus);
            toast({
              title: "Error",
              description: "Failed to unfollow user. Please try again.",
              variant: "destructive",
            });
            console.error("Unfollow error:", error);
          },
        });
      } else {
        // Follow action
        followUser(
          {
            followerId: userId,
            followingId: stream.sellerId,
          },
          {
            onSuccess: (newFollowRecord) => {
              setIsFollowLoading(false);
              setLocalFollowStatus(newFollowRecord);
              toast({
                title: "Success",
                description: `You are now following ${stream.sellerName || "this user"}`,
              });
            },
            onError: (error) => {
              setIsFollowLoading(false);
              setLocalFollowStatus(previousFollowStatus);
              toast({
                title: "Error",
                description: "Failed to follow user. Please try again.",
                variant: "destructive",
              });
              console.error("Follow error:", error);
            },
          }
        );
      }
    } catch (error) {
      console.error("Follow/Unfollow action failed:", error);
      setIsFollowLoading(false);
      setLocalFollowStatus(previousFollowStatus);
    }
  }, [stream, userId, localFollowStatus, followUser, unfollowUser]);

  // Memoized values for performance
  const activatedProducts = useMemo(
    () => products.filter((product: Models.Document) => product.isActivated),
    [products]
  );
  const featuredProducts = useMemo(
    () =>
      stream?.featuredProducts
        ? activatedProducts.filter((p) =>
            stream.featuredProducts.includes(p.$id)
          )
        : [],
    [activatedProducts, stream?.featuredProducts]
  );

  const displayStatus = useMemo(
    () =>
      stream?.status === "ended" || streamStatus === "ended" ? "ended" : "live",
    [stream?.status, streamStatus]
  );

  // Format viewer count
  const formatViewerCount = useCallback((count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  }, []);

  // Detect message priority
  const detectMessagePriority = useCallback(
    (text: string): "normal" | "question" | "purchase" => {
      const lowerText = text.toLowerCase();

      // Enhanced purchase intent keywords
      const purchaseKeywords = [
        "buy",
        "purchase",
        "order",
        "cart",
        "checkout",
        "price",
        "cost",
        "how much",
        "available",
        "stock",
        "quantity",
        "shipping",
        "delivery",
        "payment",
        "pay",
        "sell",
        "discount",
        "offer",
        "deal",
        "rupees",
        "₹",
        "rs",
        "inr",
        "money",
        "expensive",
        "cheap",
        "afford",
        "book",
        "reserve",
      ];

      if (purchaseKeywords.some((keyword) => lowerText.includes(keyword))) {
        return "purchase";
      }

      // Enhanced question keywords
      const questionKeywords = [
        "?",
        "how",
        "what",
        "when",
        "where",
        "why",
        "can",
        "could",
        "would",
        "is",
        "are",
        "will",
        "does",
        "do",
        "which",
        "who",
        "whose",
        "help",
        "explain",
        "tell",
        "show",
        "demo",
        "review",
        "opinion",
        "think",
        "recommend",
        "suggest",
        "advice",
        "guide",
        "tutorial",
      ];

      if (questionKeywords.some((keyword) => lowerText.includes(keyword))) {
        return "question";
      }

      return "normal";
    },
    []
  );

  // 🔧 FIXED: Enhanced floating message system with proper cleanup
  const handleNewFloatingMessage = useCallback(
    (message: ChatMessage) => {
      if (!isCreator) return; // Only show floating messages to seller

      const priority = detectMessagePriority(message.text);
      const displayDuration =
        priority === "normal" ? 5000 : priority === "question" ? 8000 : 10000;
      const displayUntil = Date.now() + displayDuration;

      const floatingMessage: FloatingMessage = {
        ...message,
        priority,
        isVisible: true,
        displayUntil,
      };

      setFloatingMessages((prev) => {
        // Remove oldest messages if we have more than 3
        const updated = [...prev, floatingMessage];
        if (updated.length > 3) {
          updated.shift();
        }
        return updated;
      });

      // Set up automatic hiding
      const hideTimeout = setTimeout(() => {
        setFloatingMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id ? { ...msg, isVisible: false } : msg
          )
        );

        // Remove completely after fade animation
        setTimeout(() => {
          setFloatingMessages((prev) =>
            prev.filter((msg) => msg.id !== message.id)
          );
          floatingMessageTimeouts.current.delete(message.id);
        }, 500);
      }, displayDuration);

      // Store timeout for cleanup
      floatingMessageTimeouts.current.set(message.id, hideTimeout);
    },
    [isCreator, detectMessagePriority]
  );

  // 🔧 FIXED: Clean up floating message timeouts
  const cleanupFloatingMessages = useCallback(() => {
    floatingMessageTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    floatingMessageTimeouts.current.clear();
    setFloatingMessages([]);
  }, []);

  // 🔧 FIXED: Enhanced chat toggle with message tracking
  const handleChatToggle = useCallback(() => {
    const newShowChat = !showChat;
    setShowChat(newShowChat);

    if (newShowChat) {
      // Mark all current messages as viewed
      setLastViewedMessageCount(chatMessages.length);
      setNewMessageCount(0);
    }
  }, [showChat, chatMessages.length]);

  // 🔧 FIXED: Real-time viewer tracking with proper error handling
  const trackViewer = useCallback(async () => {
    if (isCreator || viewerTrackingRef.current || !componentMountedRef.current)
      return;

    try {
      const result = await trackViewerJoin(streamId, userId);

      if (componentMountedRef.current) {
        setViewerCount(result.viewerCount);
        viewerTrackingRef.current = true;

        // Set up heartbeat with better error handling
        const heartbeatInterval = setInterval(async () => {
          if (!componentMountedRef.current || !viewerTrackingRef.current) {
            clearInterval(heartbeatInterval);
            return;
          }

          try {
            await functions.createExecution(
              appwriteConfig.trackViewerFunctionId,
              JSON.stringify({
                action: "heartbeat",
                streamId,
                userId,
                timestamp: new Date().toISOString(),
              }),
              false,
              "application/json"
            );
          } catch (error) {
            console.warn("💓 Heartbeat failed (non-critical):", error);
          }
        }, 30000);

        heartbeatIntervalRef.current = heartbeatInterval;
      }
    } catch (error) {
      console.error("❌ Error tracking viewer:", error);
    }
  }, [streamId, userId, isCreator]);

  const stopTrackingViewer = useCallback(async () => {
    if (isCreator || !viewerTrackingRef.current) return;

    try {
      // Clear heartbeat first
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      const result = await trackViewerLeave(streamId, userId);

      if (componentMountedRef.current) {
        setViewerCount(result.viewerCount);
      }

      viewerTrackingRef.current = false;
    } catch (error) {
      console.error("❌ Error stopping viewer tracking:", error);
    }
  }, [streamId, userId, isCreator]);

  useEffect(() => {
    if (!streamId || !componentMountedRef.current) return;

    const subscriptions: Array<() => void> = [];

    try {
      // 1. 🔧 FIXED: Stream document updates (viewer count, status, likes)
      const streamDocChannel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.liveStreams}.documents.${streamId}`;

      const streamUnsubscribe = client.subscribe(
        streamDocChannel,
        (response: RealtimeResponseEvent<any>) => {
          if (!componentMountedRef.current) return;

          const payload = response.payload;

          // Update viewer count in real-time
          if (payload?.viewers !== undefined) {
            const newViewerCount = parseInt(payload.viewers) || 0;
            setViewerCount(newViewerCount);
          }

          // Update stream status
          if (payload?.status) {
            setStreamStatus(payload.status);

            if (payload.status === "ended") {
              cleanupAgora();
              cleanupFloatingMessages();
            }
          }

          // Update like count in real-time
          if (payload?.likes !== undefined) {
            const newLikeCount = parseInt(payload.likes) || 0;
            setLikeCount(newLikeCount);
          }

          // Update other stream properties
          if (payload) {
            setStream((prev) => (prev ? { ...prev, ...payload } : prev));
          }
        }
      );

      subscriptions.push(streamUnsubscribe);

      // 2. 🔧 FIXED: Chat messages real-time subscription
      const chatChannel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.liveStreamsChat}.documents`;

      const chatUnsubscribe = client.subscribe(
        chatChannel,
        (response: RealtimeResponseEvent<any>) => {
          if (!componentMountedRef.current) return;

          if (
            response.events.includes(
              "databases.*.collections.*.documents.*.create"
            ) &&
            response.payload.streamId === streamId
          ) {
            const newMsg: ChatMessage = {
              id: response.payload.$id,
              userId: response.payload.userId,
              userName: response.payload.userName,
              text: response.payload.text,
              timestamp: response.payload.timestamp,
              userRole: response.payload.userRole,
              priority: detectMessagePriority(response.payload.text),
            };

            // Update chat messages immediately
            setChatMessages((prev) => [...prev, newMsg]);

            // Handle floating message for creators
            handleNewFloatingMessage(newMsg);

            // Update new message count if chat is closed
            if (!showChat) {
              setNewMessageCount((prev) => prev + 1);
            }
          }
        }
      );

      subscriptions.push(chatUnsubscribe);

      // 3. 🔧 FIXED: Product purchases real-time subscription
      const purchaseChannel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.streamPurchases}.documents`;

      const purchaseUnsubscribe = client.subscribe(
        purchaseChannel,
        (response: RealtimeResponseEvent<any>) => {
          if (!componentMountedRef.current) return;

          if (
            response.events.includes(
              "databases.*.collections.*.documents.*.create"
            ) &&
            response.payload.streamId === streamId
          ) {
            // Update products status
            setProducts((prev) =>
              prev.map((p) =>
                p.$id === response.payload.productId
                  ? { ...p, status: "sold" }
                  : p
              )
            );

            // Show purchase notification for buyers
            if (!isCreator && response.payload.buyerId === userId) {
              const product = products.find(
                (p) => p.$id === response.payload.productId
              );
              setPurchaseSuccess({
                productName: product?.title || "Product",
                productId: response.payload.productId,
                orderId: response.payload.cashfreeOrderId,
                paymentId: response.payload.$id,
              });

              setTimeout(() => setPurchaseSuccess(null), 5000);
            }
          }
        }
      );

      subscriptions.push(purchaseUnsubscribe);
    } catch (error) {
      console.error("❌ Failed to set up real-time subscriptions:", error);
    }

    // Store subscriptions for cleanup
    realtimeSubscriptions.current = subscriptions;

    return () => {
      subscriptions.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn("⚠️ Error during subscription cleanup:", error);
        }
      });
      realtimeSubscriptions.current = [];
    };
  }, [
    streamId,
    showChat,
    handleNewFloatingMessage,
    detectMessagePriority,
    isCreator,
    userId,
    products,
  ]);

  // 🔧 FIXED: Component mount tracking with proper cleanup
  useEffect(() => {
    componentMountedRef.current = true;

    return () => {
      componentMountedRef.current = false;

      // Cleanup all timeouts and intervals
      if (tokenRefreshTimer.current) {
        clearTimeout(tokenRefreshTimer.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Cleanup floating messages
      cleanupFloatingMessages();

      // Cleanup real-time subscriptions
      realtimeSubscriptions.current.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn("⚠️ Cleanup error:", error);
        }
      });

      // Stop viewer tracking and cleanup Agora
      stopTrackingViewer();
      cleanupAgora();
    };
  }, [stopTrackingViewer, cleanupFloatingMessages]);

  // 🔧 FIXED: Page visibility handling for accurate viewer tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (viewerTrackingRef.current && !isCreator) {
          stopTrackingViewer();
        }
      } else if (document.visibilityState === "visible") {
        if (
          !viewerTrackingRef.current &&
          !isCreator &&
          stream &&
          displayStatus === "live"
        ) {
          trackViewer();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isCreator, trackViewer, stopTrackingViewer, stream, displayStatus]);

  // Direct Appwrite function call for token generation
  const fetchAgoraToken = async () => {
    try {
      const payloadData = {
        channel: streamId,
        userId: userId,
        role: isCreator ? "host" : "viewer",
      };

      const execution = await functions.createExecution(
        appwriteConfig.generateAgoraTokenFunctionId,
        JSON.stringify(payloadData),
        false,
        "application/json"
      );

      if (execution.responseStatusCode !== 200) {
        throw new Error(
          `Function failed with status ${execution.responseStatusCode}: ${execution.responseBody}`
        );
      }

      let response;
      try {
        response = JSON.parse(execution.responseBody);
      } catch (e) {
        throw new Error(`Invalid JSON response: ${execution.responseBody}`);
      }

      if (!response?.token) {
        throw new Error("Token missing in response");
      }

      return {
        token: response.token,
        expiresAt: response.expiresAt || Date.now() + 7200000,
      };
    } catch (error) {
      console.error("Complete token generation error:", error);
      throw error;
    }
  };

  // Component mount tracking
  useEffect(() => {
    componentMountedRef.current = true;

    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Token management with auto-refresh
  useEffect(() => {
    if (!stream || !userId || isInitialized || !componentMountedRef.current)
      return;

    const manageToken = async () => {
      try {
        const tokenData = await fetchAgoraToken();
        if (!componentMountedRef.current) return;

        setTokenInfo(tokenData);
        setIsInitialized(true);

        const expiresIn = tokenData.expiresAt - Date.now();
        const refreshTime = Math.max(expiresIn - 600000, 60000);

        if (tokenRefreshTimer.current) {
          clearTimeout(tokenRefreshTimer.current);
        }

        tokenRefreshTimer.current = setTimeout(() => {
          if (componentMountedRef.current) {
            manageToken();
          }
        }, refreshTime);
      } catch (error) {
        if (componentMountedRef.current) {
          setError(
            error instanceof Error ? error.message : "Failed to join stream"
          );
        }
      }
    };

    manageToken();

    return () => {
      if (tokenRefreshTimer.current) {
        clearTimeout(tokenRefreshTimer.current);
      }
    };
  }, [stream, userId, isCreator, isInitialized]);

  // Cleanup Agora resources
  const cleanupAgora = async () => {
    try {
      if (clientRef.current) {
        if (clientRef.current.connectionState !== "DISCONNECTED") {
          await clientRef.current.leave();
        }
        clientRef.current = null;
      }

      localTracksRef.current.forEach((track) => {
        track?.stop();
        track?.close();
      });
      localTracksRef.current = [];
    } catch (error) {
      console.error("Cleanup error:", error);
    } finally {
      setIsStreaming(false);
      joinedStreamRef.current = false;
    }
  };

  // Initialize Agora for host
  const initAgoraHost = async () => {
    if (
      !tokenInfo ||
      initializationInProgress.current ||
      !agoraContainerRef.current ||
      !componentMountedRef.current
    )
      return;
    initializationInProgress.current = true;

    try {
      await cleanupAgora();

      const client = AgoraRTC.createClient({
        mode: "live",
        codec: "vp8",
        role: "host",
      });
      clientRef.current = client;

      client.on("user-joined", (user) => {
        console.log("User joined:", user.uid);
      });

      client.on("user-left", (user) => {
        console.log("User left:", user.uid);
      });

      await client.join(APP_ID, streamId, tokenInfo.token, userId);
      joinedStreamRef.current = true;

      const [micTrack, cameraTrack] = await Promise.all([
        micEnabled
          ? AgoraRTC.createMicrophoneAudioTrack().catch(() => null)
          : null,
        cameraEnabled
          ? AgoraRTC.createCameraVideoTrack({ encoderConfig: "720p_1" }).catch(
              () => null
            )
          : null,
      ]);

      localTracksRef.current = [micTrack, cameraTrack].filter(Boolean);
      await client.publish(localTracksRef.current.filter(Boolean));

      if (cameraTrack && agoraContainerRef.current) {
        cameraTrack.play(agoraContainerRef.current, { fit: "cover" });
      }

      if (componentMountedRef.current) {
        setIsStreaming(true);
      }
    } catch (error) {
      console.error("Host initialization failed:", error);
      if (componentMountedRef.current) {
        setError("Failed to start streaming. Please check permissions.");
      }
    } finally {
      initializationInProgress.current = false;
    }
  };

  // Initialize Agora for audience
  const initAgoraAudience = async () => {
    if (
      !tokenInfo ||
      initializationInProgress.current ||
      !agoraContainerRef.current ||
      !componentMountedRef.current
    )
      return;
    initializationInProgress.current = true;

    try {
      await cleanupAgora();

      const client = AgoraRTC.createClient({
        mode: "live",
        codec: "vp8",
        role: "audience",
      });
      clientRef.current = client;

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "video" && agoraContainerRef.current) {
          user.videoTrack?.play(agoraContainerRef.current, { fit: "cover" });
        }
        if (mediaType === "audio") {
          user.audioTrack?.play();
        }
      });

      client.on("user-unpublished", async (user, mediaType) => {
        if (mediaType === "video" && agoraContainerRef.current) {
          user.videoTrack?.stop();
        }
        if (mediaType === "audio") {
          user.audioTrack?.stop();
        }
        await client.unsubscribe(user, mediaType);
      });

      await client.join(APP_ID, streamId, tokenInfo.token, userId);
      joinedStreamRef.current = true;

      // Start tracking after successful Agora join for viewers
      if (!isCreator && componentMountedRef.current) {
        await trackViewer();
      }

      if (componentMountedRef.current) {
        setIsStreaming(true);
      }
    } catch (error) {
      console.error("Audience initialization failed:", error);
      if (componentMountedRef.current) {
        setError("Failed to join stream. Please try again.");
      }
    } finally {
      initializationInProgress.current = false;
    }
  };

  // Handle component unmount and page navigation
  useEffect(() => {
    return () => {
      stopTrackingViewer();
      cleanupAgora();
    };
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "hidden" &&
        viewerTrackingRef.current &&
        !isCreator
      ) {
        stopTrackingViewer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isCreator]);

  // Toggle camera/mic
  const toggleCamera = async () => {
    if (!isCreator) return;
    const videoTrack = localTracksRef.current.find(
      (t) => t?.trackMediaType === "video"
    );
    if (videoTrack) {
      await videoTrack.setEnabled(!cameraEnabled);
      setCameraEnabled(!cameraEnabled);
    }
  };

  const toggleMic = async () => {
    if (!isCreator) return;
    const audioTrack = localTracksRef.current.find(
      (t) => t?.trackMediaType === "audio"
    );
    if (audioTrack) {
      await audioTrack.setEnabled(!micEnabled);
      setMicEnabled(!micEnabled);
    }
  };

  // Set up real-time updates for stream status and viewer count
  useEffect(() => {
    if (!streamId || !componentMountedRef.current) return;
    let unsubscribe: (() => void) | undefined;

    try {
      const channel = `collections.${appwriteConfig.liveStreams}.documents.${streamId}`;

      unsubscribe = databases.client.subscribe(
        channel,
        (response: RealtimeResponseEvent<any>) => {
          if (!componentMountedRef.current) return;

          if (response.payload?.status) {
            setStreamStatus(response.payload.status);

            if (response.payload.status === "ended") {
              cleanupAgora();
            }
          }

          // Listen to both old and new attribute names for compatibility
          if (response.payload?.viewers !== undefined) {
            const newViewerCount = parseInt(response.payload.viewers) || 0;
            setViewerCount(newViewerCount);
          } else if (response.payload?.viewers !== undefined) {
            const newViewerCount = parseInt(response.payload.viewers) || 0;
            setViewerCount(newViewerCount);
          }
        }
      );
    } catch (error) {
      console.error("Failed to subscribe to realtime updates:", error);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [streamId]);

  // End stream function
  const endStream = async () => {
    try {
      setStreamStatus("ended");

      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.liveStreams,
        streamId,
        {
          status: "ended",
          endTime: new Date().toISOString(),
        }
      );

      await cleanupAgora();
    } catch (error) {
      console.error("Error ending stream:", error);
      if (componentMountedRef.current) {
        setStreamStatus("live");
      }
    }
  };

  // Restart stream function
  const restartStream = async () => {
    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.liveStreams,
        streamId,
        {
          status: "live",
          viewers: "0",
          startTime: new Date().toISOString(),
          endTime: null,
        }
      );

      setStreamStatus("live");
      setViewerCount(0);

      viewerTrackingRef.current = false;

      const updatedStream = await getStreamById(streamId);
      if (componentMountedRef.current) {
        setStream(updatedStream);
      }

      await initAgoraHost();
    } catch (error) {
      console.error("Error restarting stream:", error);
    }
  };

  // Initialize Agora when token and stream are ready
  useEffect(() => {
    if (!isInitialized || !tokenInfo || !stream || !componentMountedRef.current)
      return;

    if (stream.status === "ended" || streamStatus === "ended") {
      return;
    }

    const init = async () => {
      try {
        if (isCreator) {
          await initAgoraHost();
        } else {
          await initAgoraAudience();
        }
      } catch (error) {
        console.error("Agora initialization error:", error);
        if (componentMountedRef.current) {
          setError("Failed to start streaming");
        }
      }
    };

    init();

    return () => {
      cleanupAgora();
    };
  }, [isInitialized, tokenInfo, stream, streamStatus, isCreator]);

  // Load stream data and set up initial state
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const [streamData, productsData] = await Promise.all([
          getStreamById(streamId),
          getStreamProducts(streamId),
        ]);

        if (isMounted && componentMountedRef.current) {
          setStream(streamData);
          setProducts(productsData);
          setFeaturedProductId(streamData.featuredProductId ?? null);

          // Use new attribute name with fallback
          const initialViewerCount =
            parseInt(streamData.viewers || streamData.viewers || "0") || 0;
          setViewerCount(initialViewerCount);

          setStreamStatus(streamData.status || "live");

          // Initialize like count from stream data
          setLikeCount(parseInt(streamData.likes || "0") || 0);

          setLoading(false);
        }
      } catch (error) {
        if (isMounted && componentMountedRef.current) {
          setError(error instanceof Error ? error.message : "Loading failed");
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [streamId]);

  // Product purchase handling
  const handleProductPurchase = (purchaseData: PurchaseData) => {
    if (isCreator) return;

    const product = products.find((p) => p.$id === purchaseData.productId);
    if (!product) return;

    setPurchaseSuccess({
      productName: product.title,
      ...purchaseData,
    });

    setProducts((prev) =>
      prev.map((p) =>
        p.$id === purchaseData.productId ? { ...p, status: "sold" } : p
      )
    );

    setTimeout(() => setPurchaseSuccess(null), 5000);
  };

  // Subscribe to purchases
  useEffect(() => {
    if (!componentMountedRef.current) return;

    return subscribeToStreamPurchases(streamId, (purchase) => {
      if (componentMountedRef.current) {
        setProducts((prev) =>
          prev.map((p) =>
            p.$id === purchase.productId ? { ...p, status: "sold" } : p
          )
        );
      }
    });
  }, [streamId]);

  // Enhanced product handling
  const handleProductClick = useCallback((product: Product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  }, []);

  // Enhanced like handling
  const handleLike = useCallback(async () => {
    if (!stream) return;

    const newLiked = !isLiked;
    const newCount = newLiked ? likeCount + 1 : likeCount - 1;

    setIsLiked(newLiked);
    setLikeCount(newCount);

    // Update in database
    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.liveStreams,
        streamId,
        {
          likes: String(Math.max(0, newCount)),
        }
      );
    } catch (error) {
      console.error("Failed to update like count:", error);
      // Revert on error
      setIsLiked(!newLiked);
      setLikeCount(likeCount);
    }
  }, [isLiked, likeCount, stream, streamId]);

  // Share functionality
  const handleShare = useCallback(async () => {
    if (!stream) return;

    const shareUrl = window.location.href;
    const shareText = `🔴 LIVE: ${stream.title}\n\nWatch ${stream.sellerName || "this"} live stream now!\n\n${shareUrl}`;

    try {
      // Try native share first (mobile)
      if (navigator.share) {
        await navigator.share({
          title: `🔴 ${stream.title}`,
          text: shareText,
          url: shareUrl,
        });
      } else {
        // Direct WhatsApp share
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(whatsappUrl, "_blank");
      }
    } catch (error) {
      // Fallback: Copy link
      await navigator.clipboard.writeText(shareUrl);
      // Add your toast notification here
    }
  }, [stream]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex text-white justify-center items-center h-screen bg-black">
        <div className="text-center p-6 bg-gray-900 rounded-lg">
          <h2 className="text-2xl font-bold mb-2">Stream Error</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-rose-600 text-white rounded-md mr-2">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stream) return null;

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Main Container */}
      <div className="relative w-full h-screen">
        {/* Video Stream Container */}
        <div className="absolute inset-0 w-full h-full">
          {displayStatus === "ended" ? (
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-gray-900 to-black">
              <div className="text-center p-8">
                <div className="w-20 h-20 bg-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-4 h-4 bg-white rounded-sm"></div>
                </div>
                <h2 className="text-2xl font-bold mb-2">Stream Ended</h2>
                <p className="text-gray-400 mb-6">This live stream has ended</p>
                {isCreator && (
                  <button
                    onClick={restartStream}
                    className="px-6 py-3 bg-rose-600 hover:bg-rose-700 rounded-full font-medium transition-colors">
                    Restart Stream
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div
                ref={agoraContainerRef}
                className="w-full h-full bg-gray-900 flex items-center justify-center relative">
                {!isStreaming && (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-gray-400">
                      {isCreator
                        ? "Starting stream..."
                        : "Connecting to stream..."}
                    </p>
                  </div>
                )}

                {/* HD indicator */}
                {isStreaming && (
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    HD
                  </div>
                )}
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none"></div>
            </>
          )}
        </div>

        {/* Floating Chat Messages Overlay - Only for Sellers */}
        {isCreator && floatingMessages.length > 0 && (
          <div className="absolute top-24 left-4 right-20 z-30 pointer-events-none">
            <div className="space-y-3">
              {floatingMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`transform transition-all duration-500 ease-out ${
                    msg.isVisible
                      ? "translate-x-0 opacity-100 scale-100"
                      : "translate-x-full opacity-0 scale-95"
                  }`}>
                  <div
                    className={`inline-flex items-center max-w-sm rounded-2xl px-4 py-3 shadow-2xl backdrop-blur-md border-2 ${
                      msg.priority === "purchase"
                        ? "bg-gradient-to-r from-green-500/95 to-emerald-500/95 border-green-300/60 text-white shadow-green-500/25"
                        : msg.priority === "question"
                          ? "bg-gradient-to-r from-orange-500/95 to-amber-500/95 border-orange-300/60 text-white shadow-orange-500/25"
                          : "bg-gradient-to-r from-gray-800/95 to-gray-700/95 border-gray-500/60 text-white shadow-gray-500/25"
                    }`}>
                    {/* 🔧 FIXED: Enhanced Priority Indicator */}
                    <div className="mr-3 flex items-center">
                      {msg.priority === "purchase" && (
                        <div className="relative">
                          <ShoppingBag size={14} className="text-green-200" />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-300 rounded-full animate-ping"></div>
                        </div>
                      )}
                      {msg.priority === "question" && (
                        <div className="relative">
                          <MessageCircle
                            size={14}
                            className="text-orange-200"
                          />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-300 rounded-full animate-pulse"></div>
                        </div>
                      )}
                      {msg.priority === "normal" && (
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                      )}
                    </div>

                    {/* Message Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-bold opacity-90 truncate max-w-20">
                          {msg.userName}
                        </span>
                        {msg.userRole === "seller" && (
                          <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">
                            HOST
                          </span>
                        )}
                        <span className="text-[10px] opacity-70">
                          {new Date(msg.timestamp).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                      </div>
                      <p className="text-sm leading-tight line-clamp-2 font-medium">
                        {msg.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optimized Early Bird Discount Banner */}
        {stream.scheduledDiscountPercent &&
          stream.scheduledDiscountMaxUsers &&
          parseInt(stream.scheduledDiscountUsedCount || "0") <
            parseInt(stream.scheduledDiscountMaxUsers) &&
          !earlyBirdDismissed && (
            <div className="absolute top-16 left-2 right-2 z-20">
              <div className="bg-gradient-to-r from-orange-500/90 to-rose-500/90 backdrop-blur-sm text-white rounded-xl shadow-2xl border border-white/20">
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-white rounded-full mr-2 animate-ping"></div>
                      <span className="font-bold text-sm">🔥 EARLY BIRD</span>
                    </div>
                    <div className="bg-white/20 px-2 py-1 rounded-lg">
                      <span className="text-xs font-medium">
                        {parseInt(stream.scheduledDiscountMaxUsers) -
                          parseInt(
                            stream.scheduledDiscountUsedCount || "0"
                          )}{" "}
                        left
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setEarlyBirdDismissed(true)}
                    className="p-1 hover:bg-white/20 rounded-full transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <div className="px-3 pb-3">
                  <div className="text-xs leading-tight">
                    <span className="font-bold">
                      {stream.scheduledDiscountPercent}% OFF
                    </span>{" "}
                    for first {stream.scheduledDiscountMaxUsers} buyers
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Top Bar with Real-time Viewer Count */}
        <div className="absolute top-0 left-0 right-0 z-20 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => window.history.back()}
                className="p-2 bg-black/40 rounded-full backdrop-blur-sm">
                <ChevronLeft size={18} />
              </button>

              {/* Status badges */}
              <div className="flex items-center space-x-2">
                {displayStatus === "live" && (
                  <div className="flex items-center bg-rose-600 px-2 py-1 rounded text-xs font-medium">
                    <div className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></div>
                    LIVE
                  </div>
                )}

                <div className="flex items-center bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs">
                  <Eye size={12} className="mr-1" />
                  <span className="font-medium">
                    {formatViewerCount(viewerCount)}
                  </span>
                  {/* 🔧 FIXED: Visual indicator for active tracking */}
                  {!isCreator && viewerTrackingRef.current && (
                    <div className="w-1 h-1 bg-green-400 rounded-full ml-1.5 animate-ping"></div>
                  )}
                </div>

                <div className="flex items-center bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs">
                  {stream.startTime &&
                  new Date().getTime() - new Date(stream.startTime).getTime() >
                    0
                    ? `${Math.floor((new Date().getTime() - new Date(stream.startTime).getTime()) / 60000)}m`
                    : "0m"}
                </div>
                <div items-center space-x-1>
                  <button
                    onClick={handleShare}
                    className="p-2 bg-black/40 backdrop-blur-sm rounded-full">
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Creator Controls */}
            {isCreator && displayStatus === "live" && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleCamera}
                  className={`p-2 rounded-full backdrop-blur-sm ${
                    cameraEnabled ? "bg-black/40" : "bg-rose-600"
                  }`}>
                  {cameraEnabled ? <Video size={16} /> : <VideoOff size={16} />}
                </button>

                <button
                  onClick={toggleMic}
                  className={`p-2 rounded-full backdrop-blur-sm ${
                    micEnabled ? "bg-black/40" : "bg-rose-600"
                  }`}>
                  {micEnabled ? <Mic size={16} /> : <MicOff size={16} />}
                </button>

                <button
                  onClick={endStream}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 rounded text-xs font-medium transition-colors">
                  End
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Right Side Actions */}
        <div className="absolute right-3 bottom-32 z-20 flex flex-col items-center space-y-4">
          {/* 🔧 FIXED: Like Button with Real-time Updates */}
          <div className="flex flex-col items-center">
            <button
              onClick={handleLike}
              className={`p-3 rounded-full backdrop-blur-sm transition-all transform hover:scale-110 ${
                isLiked
                  ? "bg-rose-500/80 text-white shadow-lg shadow-rose-500/25"
                  : "bg-black/40 text-white hover:bg-black/60"
              }`}>
              <Heart
                size={20}
                className={`${isLiked ? "fill-current animate-pulse" : ""}`}
              />
            </button>
            <span className="text-xs text-white/80 mt-1 font-medium">
              {formatViewerCount(likeCount)}
            </span>
          </div>

          {/* 🔧 FIXED: Enhanced Chat Toggle with Real-time Message Count */}
          <div className="flex flex-col items-center relative">
            <button
              onClick={handleChatToggle}
              className="p-3 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-all transform hover:scale-110 relative">
              <MessageCircle size={20} />

              {/* 🔧 FIXED: Real-time message indicators with priority colors */}
              {chatMessages.length > 0 && (
                <div className="absolute -top-1 -right-1 flex">
                  {/* Priority-based indicators */}
                  {chatMessages
                    .slice(-5)
                    .some(
                      (m) => detectMessagePriority(m.text) === "purchase"
                    ) && (
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
                  )}
                  {chatMessages
                    .slice(-5)
                    .some(
                      (m) => detectMessagePriority(m.text) === "question"
                    ) && (
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse ml-0.5"></div>
                  )}
                  {!chatMessages
                    .slice(-5)
                    .some((m) =>
                      ["purchase", "question"].includes(
                        detectMessagePriority(m.text)
                      )
                    ) &&
                    chatMessages.length > 0 && (
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                    )}
                </div>
              )}
            </button>

            <div className="flex flex-col items-center mt-1">
              <span className="text-xs text-white/80">Chat</span>
              {/* 🔧 FIXED: Real-time new message count badge */}
              {newMessageCount > 0 && !showChat && (
                <div className="mt-1 bg-rose-500 text-white text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center animate-bounce">
                  {newMessageCount > 99 ? "99+" : newMessageCount}
                </div>
              )}
              {newMessageCount === 0 && chatMessages.length > 0 && (
                <div className="mt-1 bg-blue-500/20 text-blue-400 text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {chatMessages.length > 99 ? "99+" : chatMessages.length}
                </div>
              )}
            </div>
          </div>

          {/* 🔧 FIXED: Enhanced Products Button with Real-time Count */}
          {activatedProducts.length > 0 && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => setShowProductDetails(true)}
                className="p-3 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-all transform hover:scale-110 relative">
                <ShoppingBag size={20} />
                <div className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-400 to-rose-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow-lg">
                  {activatedProducts.length}
                </div>
              </button>
              <span className="text-xs text-white/80 mt-1">Shop</span>
            </div>
          )}

          {/* Info Toggle */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowStreamDetails(!showStreamDetails)}
              className="p-3 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-all transform hover:scale-110">
              <ChevronUp
                size={20}
                className={`transition-transform duration-300 ${showStreamDetails ? "rotate-180" : ""}`}
              />
            </button>
            <span className="text-xs text-white/80 mt-1">Info</span>
          </div>
        </div>

        {/* Bottom Content Area */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-4 pointer-events-none">
          {/* Stream Info */}
          <div className="mb-4 pointer-events-auto">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold">
                  {stream.sellerName?.[0] || "B"}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-sm">
                  {stream.sellerName || "busiconn"}
                </h3>
                <p className="text-xs text-gray-300">{stream.title}</p>
              </div>
              <button
                className={`px-3 py-1.5 min-w-[80px] rounded text-xs font-medium transition-all duration-300 shadow-md group ${
                  !!localFollowStatus
                    ? "bg-gray-700/80 text-white hover:bg-rose-600 hover:text-white border border-gray-600/30"
                    : "bg-white text-black hover:bg-gray-100"
                }`}
                onClick={handleFollow}
                disabled={isFollowLoading}>
                {isFollowLoading ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                    <span className="text-[10px]">
                      {!!localFollowStatus ? "Unfollowing..." : "Following..."}
                    </span>
                  </div>
                ) : (
                  <span className="font-medium">
                    {!!localFollowStatus ? (
                      <>
                        <span className="group-hover:hidden">Following</span>
                        <span className="hidden group-hover:inline">
                          Unfollow
                        </span>
                      </>
                    ) : (
                      "Follow"
                    )}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Featured Products Display */}
          {featuredProducts.length > 0 && (
            <div className="mb-4">
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {featuredProducts.slice(0, 4).map((product) => (
                  <div
                    key={product.$id}
                    onClick={() => handleProductClick(product)}
                    className="flex-shrink-0 w-16 cursor-pointer">
                    <img
                      src={product.images?.[0] || "/placeholder-image.jpg"}
                      alt={product.title}
                      className="w-16 h-16 rounded-lg object-cover border border-white/20"
                    />
                    <p className="text-xs text-center mt-1 text-gray-300 truncate">
                      ₹{product.price}
                    </p>
                  </div>
                ))}
                {activatedProducts.length > 4 && (
                  <div className="flex-shrink-0 w-16 flex items-center justify-center">
                    <button
                      onClick={() => setShowProductDetails(true)}
                      className="w-16 h-16 rounded-lg border border-dashed border-white/40 flex items-center justify-center text-white/60">
                      <Plus size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Chat Overlay */}
        {showChat && (
          <div className="absolute bottom-0 left-0 right-0 h-2/3 z-30">
            {/* Gradient Background with Glass Effect */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/95 to-transparent backdrop-blur-sm"></div>

            {/* Chat Container */}
            <div className="relative h-full flex flex-col">
              {/* 🔧 FIXED: Enhanced Header with Real-time Stats */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <MessageCircle size={20} className="text-blue-400" />
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Live Chat</h3>
                    <p className="text-xs text-gray-400">
                      {viewerCount} {viewerCount === 1 ? "viewer" : "viewers"} •{" "}
                      {chatMessages.length} messages
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* 🔧 FIXED: Real-time message filter indicators */}
                  <div className="flex items-center space-x-1">
                    {chatMessages.filter(
                      (m) => detectMessagePriority(m.text) === "purchase"
                    ).length > 0 && (
                      <div className="flex items-center bg-green-500/20 px-2 py-1 rounded-full animate-pulse">
                        <ShoppingBag
                          size={10}
                          className="text-green-400 mr-1"
                        />
                        <span className="text-xs text-green-400 font-medium">
                          {
                            chatMessages.filter(
                              (m) =>
                                detectMessagePriority(m.text) === "purchase"
                            ).length
                          }
                        </span>
                      </div>
                    )}
                    {chatMessages.filter(
                      (m) => detectMessagePriority(m.text) === "question"
                    ).length > 0 && (
                      <div className="flex items-center bg-orange-500/20 px-2 py-1 rounded-full animate-pulse">
                        <MessageCircle
                          size={10}
                          className="text-orange-400 mr-1"
                        />
                        <span className="text-xs text-orange-400 font-medium">
                          {
                            chatMessages.filter(
                              (m) =>
                                detectMessagePriority(m.text) === "question"
                            ).length
                          }
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleChatToggle}
                    className="p-2 hover:bg-gray-800/50 rounded-full transition-colors">
                    <X size={18} className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* 🔧 FIXED: Chat Content with Real-time Updates */}
              <div className="flex-1 overflow-hidden">
                <LiveStreamChat
                  streamId={streamId}
                  userId={userId}
                  userName={userName}
                  userRole={isCreator ? "seller" : "buyer"}
                  onNewMessage={handleNewFloatingMessage}
                  onMessagesUpdate={handleChatMessagesUpdate}
                />
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Stream Details Panel */}
        {showStreamDetails && (
          <div className="absolute bottom-0 left-0 right-0 h-1/2 z-30">
            {/* Glass Effect Background */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/90 to-transparent backdrop-blur-md rounded-t-3xl border-t border-gray-700/50"></div>

            <div className="relative h-full flex flex-col">
              {/* Enhanced Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {stream.sellerName?.[0] || "B"}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {stream.sellerName || "Seller"}
                    </h3>
                    <p className="text-xs text-gray-400">Stream Details</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStreamDetails(false)}
                  className="p-2 hover:bg-gray-800/50 rounded-full transition-colors">
                  <X size={18} className="text-gray-400" />
                </button>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Stream Info Card */}
                <div className="bg-gray-800/30 rounded-2xl p-4 border border-gray-700/50">
                  <h4 className="font-semibold text-white mb-2">
                    {stream.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {stream.description}
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-3 border border-blue-500/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <Eye size={14} className="text-blue-400" />
                      <span className="text-xs text-blue-400">Viewers</span>
                    </div>
                    <div className="text-xl font-bold text-white">
                      {formatViewerCount(viewerCount)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl p-3 border border-green-500/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <ShoppingBag size={14} className="text-green-400" />
                      <span className="text-xs text-green-400">Products</span>
                    </div>
                    <div className="text-xl font-bold text-white">
                      {activatedProducts.length}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-rose-500/20 to-rose-600/20 rounded-xl p-3 border border-rose-500/30">
                    <div className="flex items-center space-x-2 mb-1">
                      <Heart size={14} className="text-rose-400" />
                      <span className="text-xs text-rose-400">Likes</span>
                    </div>
                    <div className="text-xl font-bold text-white">
                      {formatViewerCount(likeCount)}
                    </div>
                  </div>
                </div>

                {/* Stream Timeline */}
                {stream.startTime && (
                  <div className="bg-gray-800/30 rounded-2xl p-4 border border-gray-700/50">
                    <h5 className="font-medium text-white mb-3 flex items-center">
                      <div className="w-2 h-2 bg-rose-500 rounded-full mr-2 animate-pulse"></div>
                      Stream Timeline
                    </h5>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Started:</span>
                        <span className="text-white">
                          {new Date(stream.startTime).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Duration:</span>
                        <span className="text-white">
                          {Math.floor(
                            (new Date().getTime() -
                              new Date(stream.startTime).getTime()) /
                              60000
                          )}
                          m
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Status:</span>
                        <span
                          className={`flex items-center ${displayStatus === "live" ? "text-green-400" : "text-gray-400"}`}>
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${displayStatus === "live" ? "bg-green-400 animate-pulse" : "bg-gray-400"}`}></div>
                          {displayStatus.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chat Activity Summary */}
                {chatMessages.length > 0 && (
                  <div className="bg-gray-800/30 rounded-2xl p-4 border border-gray-700/50">
                    <h5 className="font-medium text-white mb-3">
                      Chat Activity
                    </h5>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-400">
                          {
                            chatMessages.filter(
                              (m) =>
                                detectMessagePriority(m.text) === "purchase"
                            ).length
                          }
                        </div>
                        <div className="text-xs text-gray-400">
                          Purchase Intent
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-400">
                          {
                            chatMessages.filter(
                              (m) =>
                                detectMessagePriority(m.text) === "question"
                            ).length
                          }
                        </div>
                        <div className="text-xs text-gray-400">Questions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-400">
                          {
                            chatMessages.filter(
                              (m) => detectMessagePriority(m.text) === "normal"
                            ).length
                          }
                        </div>
                        <div className="text-xs text-gray-400">
                          General Chat
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Debug Panel for Development */}
                {process.env.NODE_ENV === "development" && (
                  <div className="bg-yellow-500/10 rounded-2xl p-4 border border-yellow-500/30">
                    <h5 className="font-medium mb-2 text-yellow-400 flex items-center">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                      Debug Info
                    </h5>
                    <div className="text-xs space-y-1 text-yellow-200">
                      <div className="flex justify-between">
                        <span>Viewer Tracking:</span>
                        <span>
                          {viewerTrackingRef.current
                            ? "✅ Active"
                            : "❌ Inactive"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Is Creator:</span>
                        <span>{isCreator ? "✅ Yes" : "❌ No"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Heartbeat:</span>
                        <span>
                          {heartbeatIntervalRef.current
                            ? "💓 Running"
                            : "💔 Stopped"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Floating Messages:</span>
                        <span>{floatingMessages.length} active</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Stream Status:</span>
                        <span>{streamStatus}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Products Modal */}
        {showProductDetails && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
            <div className="bg-gray-900 w-full max-h-[80vh] rounded-t-3xl overflow-hidden">
              <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-lg">Shop Products</h3>
                <button
                  onClick={() => setShowProductDetails(false)}
                  className="p-2 hover:bg-gray-800 rounded-full">
                  <X size={18} />
                </button>
              </div>

              <div className="overflow-y-auto max-h-[calc(80vh-60px)]">
                <LiveStreamProducts
                  products={activatedProducts}
                  stream={stream}
                  user={{ $id: userId, name: userName }}
                  onPurchase={handleProductPurchase}
                  featuredProductId={featuredProductId || undefined}
                  isCreator={isCreator || false}
                />
              </div>
            </div>
          </div>
        )}

        {/* Purchase Success Notification */}
        {purchaseSuccess && (
          <div className="fixed top-4 left-4 right-4 z-50 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-xl shadow-2xl border border-green-400/30 animate-in slide-in-from-top duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <ShoppingBag size={16} className="text-green-200" />
              </div>
              <div className="flex-1">
                <p className="font-bold">🎉 Purchase Successful!</p>
                <p className="text-sm text-green-100">
                  {purchaseSuccess.productName}
                </p>
                <p className="text-xs text-green-200 mt-1">
                  Order ID: {purchaseSuccess.orderId}
                </p>
              </div>
              <button
                onClick={() => setPurchaseSuccess(null)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStreamViewer;
